// Iter 108 · Modo OFFLINE Fase A — persistencia local de RESPUESTAS del checklist.
//
// Guarda en IndexedDB, por auditoría (inspection id):
//   - snapshot: contexto para reabrir la auditoría SIN señal (plantilla,
//     metadatos, IA, feedback y respuestas base). Se escribe cuando la carga
//     ONLINE tiene éxito. NO precarga auditorías nuevas (eso es Fase C).
//   - pending:  las respuestas/observaciones más recientes escritas por el
//     auditor sin conexión, pendientes de subir. Se limpia tras sincronizar.
//
// Diseño defensivo: cualquier fallo de IndexedDB se traga y devuelve valores
// seguros — NUNCA debe romper el formulario ni el guardado normal con señal.
// SOLO respuestas/observaciones/datos generales. Las fotos son Fase B.

const DB_NAME = 'aigis-offline';
const DB_VERSION = 2; // v2 (Iter 110): añade store 'photos' para fotos offline (Fase B)
const STORE = 'inspections'; // keyPath 'id'
const PHOTO_STORE = 'photos'; // keyPath 'key' = `${inspectionId}::${itemId}`

function openDB() {
    return new Promise((resolve, reject) => {
        try {
            if (typeof indexedDB === 'undefined') return reject(new Error('no-indexeddb'));
            const req = indexedDB.open(DB_NAME, DB_VERSION);
            req.onupgradeneeded = () => {
                const db = req.result;
                if (!db.objectStoreNames.contains(STORE)) {
                    db.createObjectStore(STORE, { keyPath: 'id' });
                }
                if (!db.objectStoreNames.contains(PHOTO_STORE)) {
                    const ps = db.createObjectStore(PHOTO_STORE, { keyPath: 'key' });
                    ps.createIndex('by_inspection', 'inspectionId', { unique: false });
                }
            };
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error || new Error('idb-open-failed'));
        } catch (e) {
            reject(e);
        }
    });
}

async function _txStore(storeName, mode, fn) {
    let db;
    try {
        db = await openDB();
    } catch {
        return undefined;
    }
    return new Promise((resolve, reject) => {
        try {
            const tx = db.transaction(storeName, mode);
            const store = tx.objectStore(storeName);
            const result = fn(store);
            tx.oncomplete = () => { db.close(); resolve(result?.__value); };
            tx.onerror = () => { db.close(); reject(tx.error || new Error('tx-error')); };
            tx.onabort = () => { db.close(); reject(tx.error || new Error('tx-abort')); };
        } catch (e) {
            reject(e);
        }
    });
}

async function _tx(mode, fn) {
    let db;
    try {
        db = await openDB();
    } catch {
        return undefined; // sin IndexedDB → no-op seguro
    }
    return new Promise((resolve) => {
        try {
            const tx = db.transaction(STORE, mode);
            const store = tx.objectStore(STORE);
            const result = fn(store);
            tx.oncomplete = () => { db.close(); resolve(result?.__value); };
            tx.onerror = () => { db.close(); resolve(undefined); };
            tx.onabort = () => { db.close(); resolve(undefined); };
        } catch {
            resolve(undefined);
        }
    });
}

function _reqValue(request) {
    const holder = { __value: undefined };
    request.onsuccess = () => { holder.__value = request.result; };
    return holder;
}

export async function getRecord(id) {
    if (!id) return null;
    const rec = await _tx('readonly', (store) => _reqValue(store.get(id)));
    return rec || null;
}

async function _upsert(id, patch) {
    if (!id) return;
    const existing = (await getRecord(id)) || { id };
    const next = { ...existing, ...patch, id };
    await _tx('readwrite', (store) => { store.put(next); return { __value: true }; });
    return next;
}

// Contexto para reabrir sin señal (se escribe tras carga online exitosa).
export async function saveSnapshot(id, snapshot) {
    try { await _upsert(id, { snapshot, snapshotAt: Date.now() }); } catch { /* no-op */ }
}

// Respuestas locales pendientes de subir (se escribe en cada cambio sin señal).
export async function savePending(id, responses) {
    try { await _upsert(id, { pending: responses, pendingAt: Date.now() }); } catch { /* no-op */ }
}

export async function clearPending(id) {
    try {
        const rec = await getRecord(id);
        if (!rec) return;
        delete rec.pending;
        delete rec.pendingAt;
        await _tx('readwrite', (store) => { store.put(rec); return { __value: true }; });
    } catch { /* no-op */ }
}

export async function hasPending(id) {
    const rec = await getRecord(id);
    return !!(rec && rec.pending);
}

// ── Fotos offline (Fase B) ───────────────────────────────────────────────
// Guarda el BLOB de la foto en IndexedDB, por (inspección, ítem). Reintenta el
// mismo endpoint /analyze-photo al recuperar señal. NO se descarta la copia
// local hasta confirmar la subida. Los errores de cuota SÍ se propagan (para
// avisar al auditor); el resto es defensivo.
const _photoKey = (inspectionId, itemId) => `${inspectionId}::${itemId}`;

export async function savePhoto(inspectionId, itemId, { blob, question = '', standard_reference = '', mime = 'image/jpeg' }) {
    const record = {
        key: _photoKey(inspectionId, itemId),
        inspectionId, itemId, blob, question, standard_reference,
        mime, size: blob?.size || 0, createdAt: Date.now(),
    };
    // Rechaza en caso de QuotaExceededError para que el llamador avise.
    await _txStore(PHOTO_STORE, 'readwrite', (store) => { store.put(record); });
    return record;
}

export async function getPhotosForInspection(inspectionId) {
    if (!inspectionId) return [];
    try {
        const out = await _txStore(PHOTO_STORE, 'readonly', (store) => {
            const holder = { __value: [] };
            const idx = store.index('by_inspection');
            const req = idx.getAll(inspectionId);
            req.onsuccess = () => { holder.__value = req.result || []; };
            return holder;
        });
        return out || [];
    } catch { return []; }
}

export async function deletePhoto(inspectionId, itemId) {
    try {
        await _txStore(PHOTO_STORE, 'readwrite', (store) => { store.delete(_photoKey(inspectionId, itemId)); });
    } catch { /* no-op */ }
}

export async function countPhotos(inspectionId) {
    return (await getPhotosForInspection(inspectionId)).length;
}

// Estimación de almacenamiento del dispositivo (para avisar ANTES de llenarse).
export async function estimateStorage() {
    try {
        if (navigator?.storage?.estimate) {
            const { usage = 0, quota = 0 } = await navigator.storage.estimate();
            const percent = quota ? Math.round((usage / quota) * 100) : 0;
            return { usage, quota, percent, freeBytes: Math.max(0, quota - usage) };
        }
    } catch { /* no-op */ }
    return null;
}

export default {
    getRecord, saveSnapshot, savePending, clearPending, hasPending,
    savePhoto, getPhotosForInspection, deletePhoto, countPhotos, estimateStorage,
};
